import { Suspense, useContext, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import App from "./App";
import { AuthContext } from "./context/AuthContext";
import { isAppSite } from "./utils/siteDetect";

/* ─── Route-based code splitting ─── */
const LandingPage = lazy(() => import(/* webpackChunkName: "landing" */ "./pages/Landing/LandingPage"));
const SignInPage = lazy(() => import(/* webpackChunkName: "auth" */ "./pages/Authentication/SignInPage"));
const SignUpPage = lazy(() => import(/* webpackChunkName: "auth" */ "./pages/Authentication/SignUpPage"));
const DashboardPage = lazy(() => import(/* webpackChunkName: "portal-dashboard" */ "./pages/Portal/DashboardPage"));
const ClassificationsPage = lazy(() => import(/* webpackChunkName: "portal-classifications" */ "./pages/Portal/ClassificationsPage"));
const LiveStreamPage = lazy(() => import(/* webpackChunkName: "portal-livestream" */ "./pages/Portal/LiveStreamPage"));
const SettingsPage = lazy(() => import(/* webpackChunkName: "portal-settings" */ "./pages/Portal/SettingsPage"));
const TestClassificationPage = lazy(() => import(/* webpackChunkName: "portal-test" */ "./pages/Portal/TestClassificationPage"));

const loadingFallback = <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', fontFamily: "'Inconsolata', monospace" }}>Loading…</div>;

function PrivateRoute({ children }) {
  const { currentUser, authLoading } = useContext(AuthContext);
  if (authLoading) return loadingFallback;
  return currentUser ? children : <Navigate to="/sign-in" />;
}

/* ─── Domain detection ─── */
const isLandingSite = !isAppSite;

function ExternalRedirect({ to }) {
  window.location.replace(to);
  return null;
}

function AppRoutes() {
  return (
    <div>
      <BrowserRouter>
        <Suspense fallback={loadingFallback}>
          <Routes>
            {isLandingSite ? (
              <>
                {/* Landing site: marketing page only */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/sign-in" element={<ExternalRedirect to="https://app.ecotrack.ai/sign-in" />} />
                <Route path="/sign-up" element={<ExternalRedirect to="https://app.ecotrack.ai/sign-up" />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            ) : (
              <>
                {/* App site: auth + portal */}
                <Route path="/" element={<Navigate to="/sign-in" replace />} />
                <Route path="/sign-in" element={<App childComp={<SignInPage />} />} />
                <Route path="/sign-up" element={<App childComp={<SignUpPage />} />} />

                {/* Portal (protected) */}
                <Route path="/dashboard" element={<PrivateRoute><App childComp={<DashboardPage />} /></PrivateRoute>} />
                <Route path="/classifications" element={<PrivateRoute><App childComp={<ClassificationsPage />} /></PrivateRoute>} />
                <Route path="/live-stream" element={<PrivateRoute><App childComp={<LiveStreamPage />} /></PrivateRoute>} />
                <Route path="/settings" element={<PrivateRoute><App childComp={<SettingsPage />} /></PrivateRoute>} />
                <Route path="/test-classification" element={<PrivateRoute><App childComp={<TestClassificationPage />} /></PrivateRoute>} />
              </>
            )}
          </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default AppRoutes;
